// Management Panel Types - POC v2 Transfer

export interface Address {
  street: string
  buildingNumber: string
  flatNumber?: string
  city: string
  zipCode: string
  voivodeship?: string
  district?: string
  community?: string
}

export interface Tenant {
  id: string
  name: string              // investor_name w bazie
  slug: string
  legalForm?: string        // legal_form w bazie - forma prawna (Sp. z o.o., S.A., etc.)
  nip?: string              // investor_nip w bazie
  regon?: string
  krs?: string              // investor_krs w bazie
  address: Address
  contact: {
    email: string           // investor_email w bazie - WYMAGANE
    phone: string           // investor_phone w bazie - WYMAGANE
    website?: string        // investor_website w bazie
  }
  deploymentEmail?: string  // deployment_email w bazie - email dla wdrożenia dane.gov
  createdAt: string
}

export interface InvestmentStage {
  id: string
  name: string
}

export interface InvestmentContact {
  website?: string
  email?: string
  phone?: string
}

export interface InvestmentModules {
  govReporting: boolean      // Moduł raportowania dane.gov - obowiązkowy
  developerWidget: boolean   // Moduł widżetu deweloperskiego - opcjonalny
}

// Konfiguracja pola widżetu - określa które pole i w jakiej kolejności wyświetlać
export interface WidgetFieldConfig {
  field: WidgetFieldKey       // Klucz pola (np. 'rooms_total', 'floor_number')
  order: number               // Kolejność wyświetlania (1, 2, 3...)
  enabled: boolean            // Czy pole jest włączone
}

// Dostępne pola widżetu (zgodne z aplikacją widżetu)
export type WidgetFieldKey =
  | 'area'                  // Metraż
  | 'rooms'                 // Pokoje
  | 'floor'                 // Piętro (słowne: "Parter", "1 piętro")
  | 'floor_numeric'         // Piętro (numeryczne: 0, 1, 2)
  | 'garden_plot_balcony'   // Ogród/Działka/Balkon (priorytetowe)
  | 'garden_or_plot'        // Ogród lub Działka
  | 'terrace'               // Taras
  | 'parking'               // Miejsca parkingowe
  | 'plot'                  // Działka

// Etykiety dla pól widżetu
export const WIDGET_FIELD_LABELS: Record<WidgetFieldKey, string> = {
  'area': 'Metraż',
  'rooms': 'Pokoje',
  'floor': 'Piętro (słowne)',
  'floor_numeric': 'Piętro (numeryczne)',
  'garden_plot_balcony': 'Ogród/Działka/Balkon',
  'garden_or_plot': 'Ogród lub Działka',
  'terrace': 'Taras',
  'parking': 'Miejsca parkingowe',
  'plot': 'Działka'
}

// Opisy pól widżetu (dla tooltipów)
export const WIDGET_FIELD_DESCRIPTIONS: Record<WidgetFieldKey, string> = {
  'area': 'Wyświetla metraż lokalu w m²',
  'rooms': 'Wyświetla liczbę pokoi (lub zakres np. "3 lub 4")',
  'floor': 'Wyświetla piętro słownie (np. "Parter", "1 piętro")',
  'floor_numeric': 'Wyświetla numer piętra (0, 1, 2...)',
  'garden_plot_balcony': 'Wyświetla działkę, ogród lub balkon (pierwszy dostępny)',
  'garden_or_plot': 'Wyświetla ogród lub działkę (bez balkonu)',
  'terrace': 'Wyświetla informację o tarasie',
  'parking': 'Wyświetla miejsca parkingowe (na liście: suma, na karcie: szczegóły)',
  'plot': 'Wyświetla powierzchnię działki'
}

// Domyślna konfiguracja pól dla listy (zgodna z widżetem)
export const DEFAULT_WIDGET_LIST_FIELDS: WidgetFieldConfig[] = [
  { field: 'floor', order: 1, enabled: true },
  { field: 'area', order: 2, enabled: true },
  { field: 'garden_plot_balcony', order: 3, enabled: true },
  { field: 'rooms', order: 4, enabled: true }
]

// Domyślna konfiguracja pól dla karty (zgodna z widżetem)
export const DEFAULT_WIDGET_CARD_FIELDS: WidgetFieldConfig[] = [
  { field: 'area', order: 1, enabled: true },
  { field: 'rooms', order: 2, enabled: true },
  { field: 'floor', order: 3, enabled: true },
  { field: 'garden_or_plot', order: 4, enabled: true },
  { field: 'terrace', order: 5, enabled: true },
  { field: 'parking', order: 6, enabled: true }
]

export interface ManagedInvestment {
  id: string
  tenantId: string
  name: string
  slug: string
  status: 'DRAFT' | 'ACTIVE'
  salesStartDate?: string
  stages: InvestmentStage[]
  investmentAddress: Address
  salesOfficeAddress: Address
  contact: InvestmentContact
  modules: InvestmentModules
  // Konfiguracja pól widżetu (opcjonalna - używa domyślnych jeśli brak)
  widgetListFields?: WidgetFieldConfig[]   // Pola wyświetlane na liście lokali
  widgetCardFields?: WidgetFieldConfig[]   // Pola wyświetlane na karcie lokalu
}

import { PropertyType, PropertyStatus } from '@repo/domain'

// Backward-compatible aliases (panel uses UnitType/UnitStatus naming)
export type UnitType = PropertyType
export type UnitStatus = PropertyStatus

export const UNIT_TYPE_LABELS: Record<UnitType, string> = {
  [PropertyType.LokalMieszkalny]: 'Lokal mieszkalny',
  [PropertyType.Komorka]: 'Komórka',
  [PropertyType.MiejscePostojowe]: 'Miejsce postojowe',
  [PropertyType.Garaz]: 'Garaż',
  [PropertyType.LokalUslugowy]: 'Lokal usługowy',
  [PropertyType.House]: 'Dom',
  [PropertyType.MiejscePodWiata]: 'Miejsce pod wiatą',
  [PropertyType.BoksGarazowy]: 'Boks garażowy',
}

export const UNIT_TYPE_COLORS: Record<UnitType, string> = {
  [PropertyType.LokalMieszkalny]: 'bg-blue-100 text-blue-800',
  [PropertyType.Komorka]: 'bg-yellow-100 text-yellow-800',
  [PropertyType.MiejscePostojowe]: 'bg-gray-100 text-gray-800',
  [PropertyType.Garaz]: 'bg-slate-100 text-slate-800',
  [PropertyType.LokalUslugowy]: 'bg-orange-100 text-orange-800',
  [PropertyType.House]: 'bg-purple-100 text-purple-800',
  [PropertyType.MiejscePodWiata]: 'bg-indigo-100 text-indigo-800',
  [PropertyType.BoksGarazowy]: 'bg-slate-200 text-slate-700',
}

export const UNIT_STATUS_LABELS: Record<UnitStatus, string> = {
  [PropertyStatus.Available]: 'Dostępny',
  [PropertyStatus.Reserved]: 'Rezerwacja',
  [PropertyStatus.Sold]: 'Sprzedany',
  [PropertyStatus.ComingSoon]: 'Wkrótce',
}

export const UNIT_STATUS_COLORS: Record<UnitStatus, string> = {
  [PropertyStatus.Available]: 'bg-green-100 text-green-800',
  [PropertyStatus.Reserved]: 'bg-yellow-100 text-yellow-800',
  [PropertyStatus.Sold]: 'bg-red-100 text-red-800',
  [PropertyStatus.ComingSoon]: 'bg-blue-50 text-blue-600',
}

export interface AdditionalComponent {
  id: string
  name: string
  price: number
}

// Koszt dodatkowy (parking, komórka, garaż, etc.)
export interface AdditionalCost {
  id?: string           // ID z bazy (undefined dla nowych)
  cost_type: string     // parking, storage, garage, komorka, miejsce-postojowe, commission, other
  description: string   // Oznaczenie np. "MP-12", "Komórka K-5"
  amount: number        // Kwota w PLN
  area?: number         // Metraż (opcjonalnie dla pomieszczeń)
}

export interface UnitHistoryEntry {
  id: string
  date: string
  type: 'STATUS_CHANGE' | 'PRICE_CHANGE' | 'NOTE'
  previousValue?: string
  value: string
  note?: string
  userId?: string
}

// Dane widżetu deweloperskiego (pola wyświetlane na liście/karcie lokalu)
export interface WidgetData {
  // Pokoje
  rooms_total?: number | null
  rooms_total_extra_variant?: number | null  // np. "3 lub 4" pokoje
  // Piętro
  floor_number?: number | null
  // Balkon/Taras
  has_balcony?: boolean
  has_terrace?: boolean
  // Ogród
  has_garden?: boolean
  garden_area?: number | null
  // Działka
  has_plot?: boolean
  plot_area?: number | null
  // Parking
  parking_spots?: number | null
  garage_spots?: number | null
  // Assety (referencje do property_medias)
  main_image_id?: string | null
  card_image_id?: string | null
  image_2d_id?: string | null
  // Price badge
  is_price_badge_hidden?: boolean
}

// Asset lokalu (z tabeli property_medias)
export interface PropertyMedia {
  id: string
  property_id: string
  file_url: string
  alt_text?: string
  title?: string
  type: 'image' | 'document'
  sort_order: number
  is_active: boolean
}

export interface SalesUnit {
  id: string
  investmentId: string
  stageId: string
  templateId?: string
  type: UnitType
  status: UnitStatus
  number: string
  area: number
  basePrice: number
  additionalComponents: AdditionalComponent[]
  additionalCosts?: AdditionalCost[]  // Koszty dodatkowe (parking, komórka, etc.)
  costsToDelete?: string[]            // ID kosztów do usunięcia przy edycji
  widgetData?: WidgetData             // Dane dla widżetu deweloperskiego
  history: UnitHistoryEntry[]
}

export type ManagementView = 'COMPANIES' | 'INVESTMENTS' | 'UNITS' | 'SALES'

export interface NavigationState {
  view: ManagementView
  selectedCompanyId: string | null
  selectedInvestmentId: string | null
}

// Template types for bulk unit creation
export interface UnitTemplate {
  id: string
  name: string
  type: UnitType
  area: number
  basePrice: number
  additionalComponents: Omit<AdditionalComponent, 'id'>[]
}
